import {
  CartItem as ProtoCartItem,
  WishlistItem as ProtoWishlistItem,
  WishlistItemSnapshot as ProtoWishlistItemSnapshot,
} from '@italist/grpc-proto/entities/cart_pb';
import {
  GetCartRequest,
  GetWishlistRequest,
  Identification,
  GetCartResponse,
  GetWishlistResponse,
  SetUserIdRequest,
  // CartUpdateResponse,
} from '@italist/grpc-proto/services/cart_pb';
import { CartItem, WishlistItem, ProductSnapshot } from '../../models/cart';
import { decorateProduct } from './product';
import { decorateCategory } from './categories';
import { promisify } from './promisify';
import { getAllTransformations } from '../../server/helpers/cloudImage';

export const decorateCart = (cart: ProtoCartItem[]): CartItem[] => {
  return cart.map(
    (item: ProtoCartItem): CartItem => ({
      ...item.toObject(),
      product: decorateProduct(item.getProduct()),
    }),
  );
};

export const decorateSnapshot = (item: ProtoWishlistItemSnapshot): ProductSnapshot => {
  const protoCategoryList = item.getCategoriesList();
  const categoryPath = protoCategoryList.map(protoCategory => decorateCategory(protoCategory, protoCategoryList));

  const category = categoryPath[0];
  return {
    ...item.toObject(),
    category,
    categoryPath,
    imageUrls: getAllTransformations(item.getImage()),
  };
};

export const decorateWishlist = (wishlist: ProtoWishlistItem[]): WishlistItem[] => {
  return wishlist.map(
    (item: ProtoWishlistItem): WishlistItem => {
      const protoProduct = item.getProduct();
      const protoSnapshot = item.getSnapshot();
      let product = null;
      let snapshot = null;
      if (protoProduct !== undefined) {
        product = decorateProduct(protoProduct);
      }
      if (protoSnapshot !== undefined) {
        snapshot = decorateSnapshot(protoSnapshot);
      }
      return {
        ...item.toObject(),
        product,
        snapshot,
      };
    },
  );
};

/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["session"] }] */
export const cacheCartCounter = (session, totalCartItems) => {
  session.cart_items = totalCartItems;
};

export const cacheCartCounterFromCartItems = (session, cartItems) => {
  let count = 0;
  cartItems.forEach(element => {
    count += element.quantity;
  });
  cacheCartCounter(session, count);
};

export const getCachedCartCounter = session => {
  return session.cart_items;
};

export const getCart = async (
  userId: string,
  sessionId: string,
  countryCode: string,
  billingCurrency?: string,
  alternativeCurrencies?: string[],
): Promise<CartItem[] | null> => {
  const cartClient = (await import('../services/cart')).default;
  const req = new GetCartRequest();
  const identification = new Identification();
  identification.setSessionId(sessionId);
  if (userId !== '') {
    identification.setUserId(userId);
  }
  req.setIdentification(identification);
  req.setCountryCode(countryCode);
  req.setBillingCurrency(billingCurrency);
  req.setAlternativeCurrenciesList(alternativeCurrencies);

  const props = await promisify(cartClient.getCart.bind(cartClient), req)
    .then((svcResp: GetCartResponse) => {
      const jsonCart = decorateCart(svcResp.getItemsList());
      return jsonCart;
    })
    .catch(e => {
      console.log(e);
      return null;
    });
  return props;
};

export const getWishlist = async (userId: string, countryCode: string): Promise<WishlistItem[] | null> => {
  if (userId) {
    const cartClient = (await import('../services/cart')).default;
    const req = new GetWishlistRequest();
    req.setUserId(userId);
    req.setCountryCode(countryCode);
    const props = await promisify(cartClient.getWishlist.bind(cartClient), req)
      .then((svcResp: GetWishlistResponse) => {
        const jsonWishlist = decorateWishlist(svcResp.getItemsList());
        return jsonWishlist;
      })
      .catch(e => {
        console.log(e);
        return null;
      });
    return props;
  }
  return [];
};

export const setUserId = async (userId: string, sessionId: string): Promise<boolean> => {
  const cartClient = (await import('../services/cart')).default;
  const req = new SetUserIdRequest();
  req.setUserId(userId);
  req.setSessionId(sessionId);
  const success = await promisify(cartClient.setUserId.bind(cartClient), req)
    .then(() => {
      return true;
    })
    .catch(e => {
      console.log(e);
      return false;
    });
  return success;
};
